import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Zap, Calendar, Edit3, Trash2, ArrowLeft, Clock, Trophy } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import Header from "@/components/Header";

const CATEGORIES = ["general", "music", "dance", "comedy"];

export default function AdminAutoContest() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminCheckFailed, setAdminCheckFailed] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // Override form
  const [override, setOverride] = useState({
    title: "", theme: "", description: "", category: "general",
  });

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/auto-contest/config`, { credentials: "include" });
      if (res.status === 403) { setAdminCheckFailed(true); return; }
      if (res.ok) setConfig(await res.json());
    } catch (e) {
      toast.error("Failed to load config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handlePreview = async () => {
    setBusy(true); setPreview(null);
    try {
      const res = await fetch(`${API}/admin/auto-contest/preview`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setPreview(data.theme);
        toast.success("Preview generated");
      } else toast.error("Preview failed");
    } catch { toast.error("Preview failed"); }
    setBusy(false);
  };

  const handleTriggerNow = async () => {
    if (!window.confirm("Run the FULL weekly job NOW? This will close any overdue contest, pay out winners, create a fresh contest, and notify all users.")) return;
    setBusy(true);
    try {
      const body = override.title.trim() || override.theme.trim() ? override : {};
      const res = await fetch(`${API}/admin/auto-contest/trigger-now`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created: ${data.result.contest.title}`);
        fetchConfig();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Trigger failed");
      }
    } catch { toast.error("Trigger failed"); }
    setBusy(false);
  };

  const handleSaveOverride = async () => {
    if (!override.title.trim() && !override.theme.trim()) {
      toast.error("Add at least a title or theme");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/auto-contest/override`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(override),
      });
      if (res.ok) { toast.success("Override saved — next Monday will use it"); fetchConfig(); }
      else toast.error("Save failed");
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const handleClearOverride = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/auto-contest/override`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) { toast.success("Override cleared"); fetchConfig(); }
      else toast.error("Clear failed");
    } catch { toast.error("Clear failed"); }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000033] text-[#FF66FF]">
        <Header />
        <div className="container mx-auto px-4 py-10 text-center">Loading…</div>
      </div>
    );
  }
  if (adminCheckFailed) {
    return (
      <div className="min-h-screen bg-[#000033] text-[#FF66FF]">
        <Header />
        <div className="container mx-auto px-4 py-10 max-w-md">
          <div className="brutal-card p-6 text-center">
            <h2 className="font-bold text-[#FF0000] mb-2">Admin only</h2>
            <p className="text-xs">You need to be signed in as an admin to use this page.</p>
          </div>
        </div>
      </div>
    );
  }

  const pendingOverride = config?.next_run_override;

  return (
    <div className="min-h-screen bg-[#000033] text-[#FF66FF]">
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <button onClick={() => navigate(-1)} className="myspace-btn text-xs mb-3 flex items-center gap-1" data-testid="auto-contest-back">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <h1 className="text-3xl font-bold glitter-text mb-1" data-testid="auto-contest-title">
          Auto Weekly Contests
        </h1>
        <p className="text-sm text-[#FF66FF] mb-5">
          The scheduler fires every <b>Monday 00:00 UTC</b>. Detects trending hashtag → category → AI fallback.
        </p>

        {/* Last run */}
        <div className="brutal-card p-4 mb-4">
          <h2 className="font-bold text-[#00FFFF] mb-2 flex items-center gap-1">
            <Clock className="w-4 h-4" /> Last Run
          </h2>
          {config?.last_run_at ? (
            <div className="text-xs space-y-1" data-testid="auto-contest-lastrun">
              <p>📅 <b>{new Date(config.last_run_at).toLocaleString()}</b></p>
              <p>🎯 Source: <span className="text-[#FFD700]">{config.last_source || "—"}</span></p>
              <p>🆔 Contest ID: <span className="text-[#888]">{config.last_contest_id || "—"}</span></p>
              <p>🗂 Auto-closed previous: <b>{config.last_closed_count ?? 0}</b></p>
            </div>
          ) : (
            <p className="text-xs text-[#888]">No runs yet. Tap "Trigger Now" to fire it manually.</p>
          )}
        </div>

        {/* Preview */}
        <div className="brutal-card p-4 mb-4">
          <h2 className="font-bold text-[#00FFFF] mb-2 flex items-center gap-1">
            <Sparkles className="w-4 h-4" /> What would the next theme be?
          </h2>
          <p className="text-xs text-[#FF66FF] mb-2">Dry-run only — no writes, no notifications.</p>
          <button onClick={handlePreview} disabled={busy} className="myspace-btn text-xs disabled:opacity-50" data-testid="auto-contest-preview-btn">
            {busy ? "..." : "Preview next theme"}
          </button>
          {preview && (
            <div className="mt-3 p-3 border-2 border-[#FF00FF] bg-[rgba(255,0,255,0.1)] text-xs space-y-1" data-testid="auto-contest-preview-result">
              <p className="font-bold text-[#FFD700]">{preview.title}</p>
              <p>{preview.theme}</p>
              <p className="text-[#888]">{preview.description}</p>
              <p className="text-[10px] uppercase text-[#00FFFF]">
                category: {preview.category} · source: {preview._source}
              </p>
            </div>
          )}
        </div>

        {/* Override */}
        <div className="brutal-card p-4 mb-4">
          <h2 className="font-bold text-[#00FFFF] mb-2 flex items-center gap-1">
            <Edit3 className="w-4 h-4" /> Override Next Week
          </h2>
          {pendingOverride ? (
            <div className="mb-3 p-2 border-2 border-[#FFD700] bg-[rgba(255,215,0,0.1)] text-xs">
              <p className="font-bold text-[#FFD700]">⚠️ Override pending for next Monday:</p>
              <p>· Title: {pendingOverride.title || "(none)"}</p>
              <p>· Theme: {pendingOverride.theme || "(none)"}</p>
              <p>· Category: {pendingOverride.category || "general"}</p>
              <button onClick={handleClearOverride} disabled={busy} className="mt-2 myspace-btn text-xs text-[#FF0000] flex items-center gap-1" data-testid="auto-contest-override-clear">
                <Trash2 className="w-3 h-3" /> Clear override
              </button>
            </div>
          ) : (
            <p className="text-xs text-[#FF66FF] mb-2">No override — Monday will use auto-detection.</p>
          )}

          <div className="space-y-2">
            <input
              type="text" maxLength={80}
              value={override.title}
              onChange={(e) => setOverride({ ...override, title: e.target.value })}
              placeholder="Title (e.g. Halloween Spooky-Off)"
              className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-white text-sm"
              data-testid="auto-contest-override-title"
            />
            <textarea
              rows={2} maxLength={300}
              value={override.theme}
              onChange={(e) => setOverride({ ...override, theme: e.target.value })}
              placeholder="Theme prompt (e.g. Show us your best costume in 15s)"
              className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-white text-sm"
              data-testid="auto-contest-override-theme"
            />
            <textarea
              rows={2} maxLength={500}
              value={override.description}
              onChange={(e) => setOverride({ ...override, description: e.target.value })}
              placeholder="Description / hype (2 sentences)"
              className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-white text-sm"
              data-testid="auto-contest-override-desc"
            />
            <select
              value={override.category}
              onChange={(e) => setOverride({ ...override, category: e.target.value })}
              className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-white text-sm"
              data-testid="auto-contest-override-category"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={handleSaveOverride} disabled={busy} className="myspace-btn text-xs disabled:opacity-50" data-testid="auto-contest-override-save">
              Save override for next Monday
            </button>
          </div>
        </div>

        {/* Trigger now */}
        <div className="brutal-card p-4">
          <h2 className="font-bold text-[#FFD700] mb-2 flex items-center gap-1">
            <Zap className="w-4 h-4" /> Manual Trigger (run now)
          </h2>
          <p className="text-xs text-[#FF66FF] mb-2">
            Closes overdue contests + pays winners + creates a new contest immediately. Uses the override fields above if filled, otherwise auto-detects.
          </p>
          <button onClick={handleTriggerNow} disabled={busy} className="myspace-btn-primary text-sm disabled:opacity-50 flex items-center gap-1" data-testid="auto-contest-trigger-btn">
            <Trophy className="w-4 h-4" />
            {busy ? "Running…" : "Run weekly job NOW"}
          </button>
        </div>
      </div>
    </div>
  );
}
